/**
 * Lists that grow when the reader reaches the bottom of them.
 *
 * The dashboard used to render whatever the loader returned and stop there —
 * six rows, no way to see the seventh. Sending every row instead is the other
 * wrong answer: the screen a platform with four hundred clinics opens should
 * cost what the screen with four costs, and nobody reads four hundred rows.
 *
 * So: the loader ships the first page, and each page after it is fetched only
 * once the reader has actually scrolled to the end of the one before. The
 * button is not a fallback for the observer — it is the keyboard path, and it
 * is what a reader who wants the next page *now* presses.
 *
 * A short first page is a special case worth naming. Five rows do not fill a
 * screen, so the foot of the list is already in view and an observer would
 * fetch page two before the reader has read row one — which is the opposite of
 * "show the top five". `autoLoad: false` holds the observer until the reader
 * has asked once; after that the list behaves like any other.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { get } from './api.ts';
import { cachedGet } from './cache.ts';

interface Page<T> {
  items: T[];
}

export interface Paged<T> {
  items: T[];
  /** Fetch the next page now. Safe to call while one is in flight. */
  more: () => void;
  /** No further request will return anything. */
  done: boolean;
  loading: boolean;
  error: boolean;
  /**
   * Put this on an element at the end of the list. When it comes into view the
   * next page is requested; leave it out and the list is button-driven.
   */
  sentinel: (node: HTMLElement | null) => void;
}

/**
 * @param path      endpoint that takes `limit` and `offset` and returns `{items}`
 * @param first     the first page, from the route loader
 * @param pageSize  how many the loader asked for — a short first page means done
 * @param autoLoad  false to require one press before scrolling loads any more
 * @param tables    change-stream tables behind this list. Given, later pages go
 *                  through the same stale-while-revalidate cache the loaders
 *                  use, so scrolling back down a list costs nothing and a row
 *                  that changes still invalidates the page holding it.
 */
export function usePaged<T>(
  path: string,
  first: T[],
  pageSize: number,
  autoLoad = true,
  tables?: readonly string[],
): Paged<T> {
  const [extra, setExtra] = useState<T[]>([]);
  const [done, setDone] = useState(first.length < pageSize);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  /** Whether the reader has asked for more at least once. See `autoLoad`. */
  const asked = useRef(false);

  /*
   * A revalidation — an SSE change event, a navigation back to this screen —
   * replaces the first page, and the pages after it were fetched against the
   * order the old first page had. Dropping them is the honest reset: keeping
   * them risks showing one clinic twice and another not at all, which is worse
   * than making the reader scroll again.
   */
  useEffect(() => {
    setExtra([]);
    setDone(first.length < pageSize);
    setError(false);
    asked.current = false;
  }, [first, pageSize]);

  // Read inside the callback rather than depending on them, so `more` keeps a
  // stable identity and the observer below is not torn down on every page.
  const state = useRef({ extra, done, loading });
  state.current = { extra, done, loading };

  const more = useCallback(() => {
    const { extra: loaded, done: finished, loading: busy } = state.current;
    asked.current = true;
    if (finished || busy) return;
    setLoading(true);
    setError(false);
    const offset = pageSize + loaded.length;
    const url = `${path}${path.includes('?') ? '&' : '?'}limit=${pageSize}&offset=${offset}`;
    void (tables ? cachedGet<Page<T>>(url, tables) : get<Page<T>>(url))
      .then((page) => {
        setExtra((current) => [...current, ...page.items]);
        if (page.items.length < pageSize) setDone(true);
      })
      .catch(() => {
        // Stop asking. An observer that retries on every scroll pixel turns one
        // failed request into a hundred.
        setError(true);
        setDone(true);
      })
      .finally(() => setLoading(false));
  }, [path, pageSize, tables]);

  const observer = useRef<IntersectionObserver | null>(null);
  const sentinel = useCallback(
    (node: HTMLElement | null) => {
      observer.current?.disconnect();
      if (!node) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (!autoLoad && !asked.current) return;
          if (entries.some((entry) => entry.isIntersecting)) more();
        },
        // Start the request just before the row is on screen, so the page is
        // usually there by the time the reader arrives at it.
        { rootMargin: '200px' },
      );
      observer.current.observe(node);
    },
    [more, autoLoad],
  );

  useEffect(() => () => observer.current?.disconnect(), []);

  return { items: [...first, ...extra], more, done, loading, error, sentinel };
}
